import { Immutable, SB, WithMeta } from '@play-co/replicant';

import { Pet } from './buruburu/pet';
import { Conversation } from './conversation';
import { Inventory } from './buruburu/inventory';
import { Tutorial } from './buruburu/tutorial';
import { Care } from './buruburu/care';
import { Profile } from './profile';
import { userSettingsSchema } from './state/settings';
import ab from './ruleset/ab';
import { Admin } from './admin';
import { Social } from './social';
import { Gifts } from './buruburu/gifts';
import { Minigame } from './minigame';
import { Match3 } from './minigame/match3';
import { Level } from './buruburu/level';
import { Encyclopedia } from './encyclopedia';
import { Visit } from './visit';
import { Fate } from './buruburu/fate';
import { Iap } from './iap';
import { Duo } from './duo';
import { History } from './buruburu/history';
import { Native } from './native';

const chatbotAdReferralSchema = SB.object({
  adId: SB.string().optional(),
  time: SB.number(),
  adTitle: SB.string().optional(),
  postId: SB.string().optional(), // Messenger only(?)
  assetId: SB.string().optional(), // IG only

  adCreativeName: SB.string().optional(),
  adSetName: SB.string().optional(),
  adCampaignName: SB.string().optional(),
});

export const stateSchema = SB.object({
  ...Pet.schema,
  ...Conversation.schema,
  ...Inventory.schema,
  ...Tutorial.schema,
  ...Care.schema,
  ...Profile.schema,
  ...Admin.schema,
  ...Social.schema,
  ...Gifts.schema,
  ...Minigame.schema,
  ...Match3.schema,
  ...Level.schema,
  ...Encyclopedia.schema,
  ...Visit.schema,
  ...Fate.schema,
  ...Iap.schema,
  ...Duo.schema,
  ...History.schema,
  ...Native.schema,

  // User's time zone, set from the client
  timeZone: SB.string().optional(),

  timezoneOffsetMs: SB.number(),

  abTests: SB.map(SB.string()),

  // chatbot message referrals
  messengerAdReferral: SB.object({
    ref: SB.string().optional(),
    postback: SB.boolean(),
  }).optional(),

  // Cooldowns map that can be used by multiple features
  cooldowns: SB.map(
    SB.object({
      timestamp: SB.int(),
    }),
  ).optional(),

  // Locale, sfx turn on, music turn on
  settings: userSettingsSchema,

  // Map of values migrated over from platform storage
  // https://blackstormlabs.atlassian.net/browse/THUG-680
  platformStorage: SB.map(SB.unknown()).optional(),

  // TODO: REMOVE NEXT VERSION.
  muteNotifications: SB.int().default(0).optional(),

  // Timestamp of user signalling intent to delete their account,
  // used in a two-step process to minimise unintended deletions
  deleteAccountIntent: SB.number().optional(),

  // The number of times the user has reached entryFinal
  entryFinalCount: SB.number().optional(),

  // The web platform where the user has been playing the game - Instagram or Messenger
  platform: SB.string().optional(),

  // Linked (page scoped) Instagram user ID, if any
  instagramId: SB.string().optional(),

  // Linked (page scoped) Facebook user ID, if any
  messengerId: SB.string().optional(),

  firstChatbotAdReferral: chatbotAdReferralSchema.optional(),
  lastChatbotAdReferral: chatbotAdReferralSchema.optional(),

  // How many times we've sent a play button message in response to a chatbot-user interaction
  chatbotPlayMessageCount: SB.int().default(0).optional(),

  // Count and timestamp for tracking messages sent to the player
  chatbotSentSinceEntry: SB.int().default(0).optional(),
  chatbotLastSentTime: SB.int().optional(),

  // Count and timestamp for for messages received from the player
  chatbotReceivedSinceEntry: SB.int().default(0).optional(),
  chatbotLastReceivedTime: SB.int().optional(),
});

export type UserSettings = SB.ExtractType<typeof userSettingsSchema>;
export type StateFromSchema = SB.ExtractType<typeof stateSchema>;

/**
 * Mutable state. Use for modifiers, messages, actions.
 */
export type MutableState = WithMeta<
  StateFromSchema,
  { abTests: typeof ab.dynamicAB }
>;

/**
 * Immutable state. Use in getters and outside replicant.
 */
export type State = Immutable<MutableState>;

export type AnyPayload = { [key: string]: any };

/**
 * Creates a default state with replicant system state fields filled in. The result is mutable, for convenience.
 *
 * @param metaFields Optional overrides for the default values for the replicant state fields.
 */
export function getDefaultState(
  metaFields?: Partial<WithMeta<{}>>,
): StateFromSchema & { -readonly [K in keyof WithMeta<{}>]: WithMeta<{}>[K] } {
  return {
    ...stateSchema.getDefault(),

    // WithMeta fields.
    id: '',
    createdAt: 0,
    updatedAt: 0,
    ruleset: { abTests: {} },
    userSharedStates: {},

    ...metaFields,
  };
}
